/** Progressive enhancement scoped to the homepage; safe to mount/unmount with React. */
export function initializeHomeMotion(root: HTMLElement, copy: (text: string) => string = text => text) {
  const abort = new AbortController();
  const observers: IntersectionObserver[] = [];
  const frames = new Set<number>();
  const media = window.matchMedia("(prefers-reduced-motion: reduce)");
  const query = <T extends HTMLElement = HTMLElement>(selector: string) => root.querySelector<T>(selector)!;
  const all = <T extends HTMLElement = HTMLElement>(selector: string) => Array.from(root.querySelectorAll<T>(selector));
  const listen = (target: EventTarget, type: string, handler: EventListener, passive = false) => {
    target.addEventListener(type, handler, { signal: abort.signal, passive });
  };
  const frame = (callback: () => void) => {
    const id = requestAnimationFrame(() => { frames.delete(id); callback(); });
    frames.add(id);
  };

  const menu = query<HTMLButtonElement>(".menu");
  const nav = query("nav");
  const closeMenu = () => {
    nav.classList.remove("open");
    menu.setAttribute("aria-expanded", "false");
    menu.textContent = copy("Menu +");
    menu.setAttribute("aria-label", copy("Ouvrir le menu"));
  };
  listen(menu, "click", () => {
    const open = menu.getAttribute("aria-expanded") !== "true";
    menu.setAttribute("aria-expanded", String(open));
    nav.classList.toggle("open", open);
    menu.textContent = copy(open ? "Fermer −" : "Menu +");
    menu.setAttribute("aria-label", copy(open ? "Fermer le menu" : "Ouvrir le menu"));
  });
  listen(nav, "click", event => { if ((event.target as Element).closest("a")) closeMenu(); });
  listen(root, "keydown", event => {
    if ((event as KeyboardEvent).key === "Escape" && nav.classList.contains("open")) {
      closeMenu(); menu.focus();
    }
  });

  const captions = ["01 — Être présent au bon moment.", "02 — Donner envie de vous contacter.", "03 — Mesurer les demandes reçues."];
  const buttons = all<HTMLButtonElement>("[data-step]");
  const panels = all("[data-panel]");
  buttons.forEach(button => listen(button, "click", () => {
    const step = Number(button.dataset.step);
    query(".acquisition-board").dataset.current = String(step);
    buttons.forEach((item, index) => {
      item.classList.toggle("active", index === step);
      item.setAttribute("aria-pressed", String(index === step));
    });
    panels.forEach((panel, index) => {
      panel.hidden = index !== step;
      panel.classList.toggle("stage-enter", index === step);
    });
    query("#journey-caption").textContent = copy(captions[step]);
    query("#journey-count").textContent = `0${step + 1} / 03`;
  }));

  const track = query("#portfolio-track");
  const slides = all(".projects .project");
  const previous = query<HTMLButtonElement>("[data-project-prev]");
  const next = query<HTMLButtonElement>("[data-project-next]");
  let current = 0;
  let scrollPending = false;
  const updateCarousel = () => {
    const origin = track.getBoundingClientRect().left;
    let distance = Infinity;
    slides.forEach((slide, index) => {
      const delta = Math.abs(slide.getBoundingClientRect().left - origin);
      if (delta < distance) { distance = delta; current = index; }
    });
    previous.disabled = current === 0;
    next.disabled = current === slides.length - 1;
    const label = `${String(current + 1).padStart(2, "0")} / ${String(slides.length).padStart(2, "0")}`;
    const count = query("#portfolio-count");
    if (count.textContent !== label) count.textContent = label;
    scrollPending = false;
  };
  const showProject = (index: number, instant = false) => {
    current = Math.max(0, Math.min(slides.length - 1, index));
    const left = slides[current].getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
    track.scrollTo({ left, behavior: media.matches || instant ? "instant" : "smooth" });
  };
  listen(previous, "click", () => showProject(current - 1));
  listen(next, "click", () => showProject(current + 1));
  listen(track, "keydown", event => {
    const key = (event as KeyboardEvent).key;
    const destinations = { ArrowLeft: current - 1, ArrowRight: current + 1, Home: 0, End: slides.length - 1 };
    if (key in destinations) { event.preventDefault(); showProject(destinations[key]); }
  });
  listen(track, "scroll", () => {
    if (!scrollPending) { scrollPending = true; frame(updateCarousel); }
  }, true);
  listen(window, "resize", () => { frame(() => { showProject(current, true); updateCarousel(); }); }, true);
  updateCarousel();

  let progress: HTMLDivElement | null = null;
  let progressPending = false;
  const updateProgress = () => {
    progressPending = false;
    if (!progress || media.matches) return;
    const available = document.documentElement.scrollHeight - window.innerHeight;
    const fraction = available > 0 ? Math.max(0, Math.min(1, window.scrollY / available)) : 0;
    progress.style.transform = `scaleX(${fraction})`;
  };
  const stopMotion = () => {
    observers.forEach(observer => observer.disconnect());
    observers.length = 0;
    root.classList.remove("motion-ready");
    all(".reveal").forEach(element => element.classList.add("in-view"));
    query(".process").classList.remove("motion-pending");
    progress?.remove(); progress = null;
  };
  const startMotion = () => {
    if (media.matches || !("IntersectionObserver" in window)) return;
    root.classList.add("motion-ready");
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add("in-view"); observer.unobserve(entry.target); }
    }), { threshold: 0.08 });
    observers.push(observer);
    all(".section-heading, .service, .offer-promise, .offer-item, .approach > div, .quote .wrap, .team article, .process-track article, .faq > div, .article-link, .contact .wrap").forEach((element, index) => {
      if (element.getBoundingClientRect().top < window.innerHeight) return;
      element.classList.add("reveal");
      element.style.setProperty("--delay", `${index % 2 * 65}ms`);
      observer.observe(element);
    });
    const process = query(".process");
    process.classList.add("motion-pending");
    const processObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        process.classList.add("process-visible"); processObserver.disconnect();
      }
    }, { threshold: 0.15 });
    processObserver.observe(process); observers.push(processObserver);
    progress = document.createElement("div");
    progress.className = "scroll-progress";
    progress.setAttribute("aria-hidden", "true");
    root.append(progress); updateProgress();
  };
  listen(window, "scroll", () => {
    if (!media.matches && !progressPending) { progressPending = true; frame(updateProgress); }
  }, true);
  listen(media, "change", () => { stopMotion(); startMotion(); });
  startMotion();

  return () => {
    abort.abort();
    frames.forEach(cancelAnimationFrame);
    stopMotion();
  };
}
